import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Divider } from 'primereact/divider'

import { RootState } from '../../../admin/redux/store'
import {
    useChargePointGetMaintenanceLogsQuery,
    useChargePointAddMaintenanceLogMutation,
    ChargePointGetMaintenanceLogsApiArg,
    useLazyChargePointGetMaintenanceLogsQuery,
    MaintenanceLog,
} from '../../../common/redux/api/web-api'
import { dateLocalize } from '../../../lib/date-localize'
import { notifyError } from '../../../common/features/notificationsManager/Notify'
import useText from '../../../common/hooks/useText'
import { isPartnerDomain } from '../../../lib/currentDomain'
import { Input } from '../../../common/uikit/Input'
import EmptyListRefresh from '../../../common/components/EmptyListRefresh'

const defaultMaintenanceLogs: ChargePointGetMaintenanceLogsApiArg = {
    chargePointId: '',
    offset: 0,
    noOfItems: 5,
}

export const ChargePointMaintenanceLogsWidget = React.memo(
    function ChargePointMaintenanceLogsWidget(): React.ReactElement {
        const text = useText()
        const { chargePointId } = useParams()
        const [logMessage, setLogMessage] = useState('')

        const [maintenanceLogs, setMaintenanceLogs] = useState<MaintenanceLog[]>([])
        const [maintenanceLogsQuery, setMaintenanceLogsQuery] = useState<ChargePointGetMaintenanceLogsApiArg>({
            ...defaultMaintenanceLogs,
        })
        const userId = useSelector((state: RootState) => state.user.id)
        const [addMaintenanceLogs, { isLoading: addLogsLoading }] = useChargePointAddMaintenanceLogMutation()
        const [loadMoreLogsRequest] = useLazyChargePointGetMaintenanceLogsQuery()

        const {
            data: currentLogs,
            isFetching,
            refetch,
        } = useChargePointGetMaintenanceLogsQuery(
            {
                chargePointId: chargePointId,
                offset: 0,
                noOfItems: 5,
                accessLevel: isPartnerDomain ? 'Partner' : 'Support',
            },
            { refetchOnMountOrArgChange: true }
        )

        useEffect(() => {
            currentLogs && setMaintenanceLogs(currentLogs)
        }, [isFetching])

        const loadMoreLogs = async () => {
            try {
                const loadMore = {
                    offset: maintenanceLogsQuery?.offset + maintenanceLogsQuery?.noOfItems,
                }

                const result = await loadMoreLogsRequest({
                    chargePointId: chargePointId,
                    accessLevel: isPartnerDomain ? 'Partner' : 'Support',
                    noOfItems: maintenanceLogsQuery.noOfItems,
                    offset: loadMore?.offset,
                }).unwrap()

                setMaintenanceLogsQuery({ ...maintenanceLogsQuery, ...loadMore })
                setMaintenanceLogs([...maintenanceLogs, ...result])
            } catch (e) {
                notifyError(text('msgCouldNotLoadMaintenanceLog'))
            }
        }

        const doAddLogs = async () => {
            try {
                await addMaintenanceLogs({
                    chargePointId: chargePointId,
                    maintenanceLog: {
                        chargePointId: chargePointId,
                        userId: userId,
                        accessLevel: isPartnerDomain ? 'Partner' : 'Support',
                        message: logMessage,
                    },
                }).unwrap()
                setMaintenanceLogs([...maintenanceLogs, logMessage])
                setLogMessage('')
            } catch (e) {
                notifyError(text('msgCouldNotSaveMaintenanceLog'))
            }
        }

        return (
            <Card title={text('lblMaintenanceLogWidget')}>
                <form name="maintenanceLogForm" role="form" className="flex gap-2 mb-3">
                    <Input
                        label={text('lblMessageField')}
                        type="text"
                        className="w-11"
                        name="message"
                        id="message"
                        value={logMessage}
                        onInput={(event) => setLogMessage(event.currentTarget.value)}
                        data-testid="message-input"
                        required
                    />
                    <div className="flex justify-content-end mt-2">
                        <Button
                            loading={addLogsLoading}
                            disabled={addLogsLoading || !logMessage}
                            label={text('btnSave')}
                            data-testid="btn-save-maintenance"
                            onClick={() => doAddLogs()}
                            className="h-3rem  mt-3"
                        />
                    </div>
                </form>

                {maintenanceLogs?.map((log) => (
                    <div data-testid="logs-message" key={log.id}>
                        {log?.message}
                        <div className="flex gap-2">
                            <span className="text-red-200">{log?.accessLevel}</span>
                            <span>{log?.sign}</span>

                            <span>{dateLocalize(log?.timestamp)}</span>
                        </div>
                        <Divider />
                    </div>
                ))}

                {maintenanceLogs?.length === 0 && (
                    <EmptyListRefresh
                        emptyText={text('lblNoMaintenanceLogsAvailable')}
                        action={refetch}
                        isFetching={isFetching}
                    />
                )}

                {maintenanceLogs?.length >= 5 && (
                    <div className="flex justify-content-center mt-2">
                        <Button
                            type="button"
                            data-testid="btn-load-more-maintenance"
                            label={text('btnLoadMore')}
                            onClick={() => loadMoreLogs()}
                        />
                    </div>
                )}
            </Card>
        )
    }
)
